import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ApplicationProgress from './components/ApplicationProgress';
import PersonalInfoSection from './components/PersonalInfoSection';
import DocumentUploadSection from './components/DocumentUploadSection';
import ApplicationQuestionsSection from './components/ApplicationQuestionsSection';
import SkillsAssessmentSection from './components/SkillsAssessmentSection';
import ApplicationReviewSection from './components/ApplicationReviewSection';
import JobDetailsCard from './components/JobDetailsCard';
import ConfirmationModal from './components/ConfirmationModal';
import { applicationService } from '../../services/applicationService';
import { supabase } from '../../lib/supabase';

const JobApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Get job details from navigation state
  const jobFromState = location?.state?.job;
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    portfolio: '',
    experience: '',
    currentCtc: '',
    expectedCtc: '',
    noticePeriod: '',
    willingToRelocate: false,
    openToRemote: true,
    whyInterested: '',
    relevantSkills: ''
  });

  const [documents, setDocuments] = useState({
    resume: null,
    coverLetter: null,
    portfolio: null,
    certificates: null
  });

  const [skills, setSkills] = useState([
  { id: 1, name: 'React.js', required: true, rating: 0 },
  { id: 2, name: 'Node.js', required: true, rating: 0 },
  { id: 3, name: 'MongoDB', required: false, rating: 0 },
  { id: 4, name: 'TypeScript', required: false, rating: 0 }]
  );

  // Load job details on mount
  useEffect(() => {
    const loadJobDetails = async () => {
      // Check for job object in state first
      if (jobFromState) {
        setJobDetails(jobFromState);
        setLoading(false);
        return;
      }

      // Check for jobId in location state (from job-details page)
      const jobIdFromState = location?.state?.jobId;
      const jobIdFromQuery = new URLSearchParams(location.search)?.get('jobId');
      const jobId = jobIdFromState || jobIdFromQuery;

      if (!jobId) {
        navigate('/job-listings');
        return;
      }

      try {
        const { data, error } = await supabase?.from('jobs')?.select(`
            *,
            user_profiles:employer_id (
              company_name
            )
          `)?.eq('id', jobId)?.single();

        if (error) throw error;

        setJobDetails({
          id: data?.id,
          title: data?.job_title,
          company: data?.user_profiles?.company_name,
          location: `${data?.city}, ${data?.state}`,
          type: data?.employment_type,
          salary: data?.salary_min && data?.salary_max 
            ? `₹${(data?.salary_min / 100000)?.toFixed(1)}L - ₹${(data?.salary_max / 100000)?.toFixed(1)}L`
            : 'Not disclosed',
          experience: data?.experience_level
        });
      } catch (error) {
        console.error('Error loading job:', error);
        navigate('/job-listings');
      } finally {
        setLoading(false);
      }
    };

    loadJobDetails();
  }, [location, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileUpload = (file, docType) => {
    const maxSize = docType === 'portfolio' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;

    if (file?.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [docType]: `File size exceeds ${docType === 'portfolio' ? '10MB' : '5MB'} limit`
      }));
      return;
    }

    const allowedTypes = docType === 'portfolio' ?
    ['application/pdf', 'application/zip'] :
    ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    if (!allowedTypes?.includes(file?.type)) {
      setErrors((prev) => ({
        ...prev,
        [docType]: 'Invalid file format'
      }));
      return;
    }

    setDocuments((prev) => ({ ...prev, [docType]: file }));
    if (errors?.[docType]) {
      setErrors((prev) => ({ ...prev, [docType]: '' }));
    }
  };

  const handleFileRemove = (docType) => {
    setDocuments((prev) => ({ ...prev, [docType]: null }));
  };

  const handleSkillRating = (skillId, rating) => {
    setSkills((prev) => prev?.map((skill) =>
    skill?.id === skillId ? { ...skill, rating } : skill
    ));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
      if (!formData?.email?.trim()) newErrors.email = 'Email is required';
      if (!formData?.location?.trim()) newErrors.location = 'Location is required';
    }

    if (step === 2) {
      // Resume is now optional - removed validation
    }

    if (step === 3) {
      if (!formData?.experience) newErrors.experience = 'Experience level is required';
      if (!formData?.currentCtc?.trim()) newErrors.currentCtc = 'Current CTC is required';
      if (!formData?.expectedCtc?.trim()) newErrors.expectedCtc = 'Expected CTC is required';
      if (!formData?.noticePeriod) newErrors.noticePeriod = 'Notice period is required';
      if (!formData?.whyInterested?.trim()) newErrors.whyInterested = 'This field is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleEdit = (section) => {
    const stepMap = {
      personal: 1,
      documents: 2,
      questions: 3,
      skills: 3
    };
    setCurrentStep(stepMap?.[section] || 1);
  };

  const handleSaveDraft = () => {
    localStorage.setItem('jobApplicationDraft', JSON.stringify({
      formData,
      documents: Object.keys(documents)?.reduce((acc, key) => {
        if (documents?.[key]) {
          acc[key] = {
            name: documents?.[key]?.name,
            size: documents?.[key]?.size
          };
        }
        return acc;
      }, {}),
      skills
    }));
    alert('Application saved as draft successfully!');
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // 1. Submit application to database
      const application = await applicationService?.submitApplication(
        jobDetails?.id,
        formData,
        skills
      );

      // 2. Send confirmation email
      try {
        const emailResponse = await supabase?.functions?.invoke('send-application-confirmation', {
          body: {
            applicantEmail: formData?.email,
            applicantName: formData?.fullName,
            jobTitle: jobDetails?.title,
            companyName: jobDetails?.company
          }
        });

        if (emailResponse?.error) {
          console.error('Email sending failed:', emailResponse?.error);
          // Continue even if email fails - application is already saved
        }
      } catch (emailError) {
        console.error('Email error:', emailError);
        // Continue - application is already saved
      }

      // 3. Clean up and navigate
      setIsSubmitting(false);
      setShowConfirmModal(false);
      localStorage.removeItem('jobApplicationDraft');
      
      navigate('/application-tracking', {
        state: {
          message: 'Application submitted successfully! You will receive a confirmation email shortly.'
        }
      });
    } catch (error) {
      setIsSubmitting(false);
      setErrors({ submit: error?.message || 'Failed to submit application' });
      console.error('Application submission error:', error);
    }
  };

  const calculateProgress = () => {
    let completed = 0;
    const total = 4;

    if (formData?.fullName && formData?.email && formData?.phone && formData?.location) completed++;
    if (documents?.resume) completed++;
    if (formData?.experience && formData?.currentCtc && formData?.expectedCtc && formData?.noticePeriod && formData?.whyInterested) completed++;
    if (currentStep === 4) completed = 4;

    return completed;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!jobDetails) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12 px-4 lg:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => navigate('/job-details', { state: { jobId: jobDetails?.id } })}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4">

              <Icon name="ArrowLeft" size={16} />
              <span>Back to Job Details</span>
            </button>

            <h1 className="text-3xl font-bold text-foreground mb-2">Apply for Position</h1>
            <p className="text-muted-foreground">Complete the application form to submit your candidacy</p>
          </div>

          <JobDetailsCard job={jobDetails} />

          <ApplicationProgress
            currentStep={calculateProgress()}
            totalSteps={4} />

          {/* Display submission error if any */}
          {errors?.submit && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-4">
              {errors?.submit}
            </div>
          )}

          {currentStep === 1 &&
          <PersonalInfoSection
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors} />

          }

          {currentStep === 2 &&
          <DocumentUploadSection
            documents={documents}
            handleFileUpload={handleFileUpload}
            handleFileRemove={handleFileRemove}
            errors={errors} />

          }

          {currentStep === 3 &&
          <>
              <ApplicationQuestionsSection
              formData={formData}
              handleInputChange={handleInputChange}
              handleSelectChange={handleSelectChange}
              handleCheckboxChange={handleCheckboxChange}
              errors={errors} />


              <SkillsAssessmentSection
              skills={skills}
              handleSkillRating={handleSkillRating} />

            </>
          }

          {currentStep === 4 &&
          <ApplicationReviewSection
            formData={formData}
            documents={documents}
            skills={skills}
            onEdit={handleEdit} />

          }

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {currentStep > 1 &&
              <Button
                variant="outline"
                onClick={handleBack}
                iconName="ChevronLeft"
                iconPosition="left"
                className="flex-1 sm:flex-none">

                  Back
                </Button>
              }

              <Button
                variant="ghost"
                onClick={handleSaveDraft}
                iconName="Save"
                iconPosition="left"
                className="flex-1 sm:flex-none">

                Save Draft
              </Button>
            </div>

            <div className="w-full sm:w-auto">
              {currentStep < 4 ?
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
                fullWidth>

                  Continue
                </Button> :

              <Button
                variant="default"
                onClick={() => setShowConfirmModal(true)}
                iconName="Send"
                iconPosition="left"
                fullWidth>

                  Submit Application
                </Button>
              }
            </div>
          </div>
        </div>
      </main>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );

};

export default JobApplication;